import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkle } from '@phosphor-icons/react'
import { useBillingStore } from '@/stores/billingStore'
import { usePermission } from '@/hooks/usePermission'
import { livePlan } from '@/lib/planStatus'

// Per-tier upgrade prompt → always nudges to the next tier up. Keys are
// Plan.name SLUGS, which match the customer-facing labels since the
// standard->basic / smb->momentum rename (the $79 plan was `standard`, then
// `starter`, and is `basic` since the 2026-10-13 Starter/Basic slug swap). The
// legacy keys are kept alongside so a plan row that has not been migrated yet
// still gets its prompt instead of silently falling back to the "free" copy.
// Enterprise (top tier) renders nothing.
const UPGRADE: Record<string, { title: string; sub: string; cta: string }> = {
  free: {
    title: 'Boost with AI',
    sub: 'AI replies, contact enrichment, and automation that save hours.',
    cta: 'Upgrade to Starter',
  },
  // Starter is the $29 entry plan: its next tier up is Basic.
  starter: {
    title: 'Boost with AI',
    sub: 'AI replies, contact enrichment, and automation that save hours.',
    cta: 'Upgrade to Basic',
  },
  basic: {
    title: 'Scale your outreach',
    sub: 'More credits, higher send limits, and team collaboration.',
    cta: 'Upgrade to Momentum',
  },
  momentum: {
    title: 'Unlock full scale',
    sub: 'Unlimited volume, priority support, and advanced controls.',
    cta: 'Upgrade to Enterprise',
  },
}

// Legacy slugs, same copy. Drop once no environment holds an un-migrated row.
UPGRADE.standard = UPGRADE.basic
UPGRADE.smb = UPGRADE.momentum

const UpgradeCard: React.FC = () => {
  const navigate = useNavigate()
  const { subscription, fetchSubscription } = useBillingStore()
  // Self-contained permission check, not just the parent's: this card fires a
  // billing fetch on mount, so it must never mount-and-fetch on the strength
  // of a stale map from a previous session (the accept-invite handoff).
  const { allowed: canViewBilling } = usePermission('billing', 'manage_subscription')
  // Always re-fetch on mount (covers login/logout) and only render once the
  // tier is known — otherwise the card flashes the default "free" copy for a
  // frame before the real subscription resolves.
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!canViewBilling) return
    let alive = true
    Promise.resolve(fetchSubscription()).finally(() => { if (alive) setReady(true) })
    return () => { alive = false }
  }, [fetchSubscription, canViewBilling])

  if (!canViewBilling) return null

  if (!ready) {
    // Skeleton placeholder — reserves space, no wrong-tier flash.
    return (
      <div className="px-2.5 pb-2">
        <div className="rounded-xl border border-[#5e6ad2]/15 dark:border-[#5e6ad2]/20 bg-[#5e6ad2]/[0.04] dark:bg-[#5e6ad2]/[0.08] p-3.5 space-y-2.5">
          <div className="skeleton-shimmer h-4 w-1/2 rounded bg-muted/70 dark:bg-white/[0.06]" />
          <div className="skeleton-shimmer h-3 w-full rounded bg-muted/70 dark:bg-white/[0.06]" />
          <div className="skeleton-shimmer h-9 w-full rounded-lg bg-muted/70 dark:bg-white/[0.06] mt-3" />
        </div>
      </div>
    )
  }

  // The plan that is RUNNING, not merely the last one reported: /subscription
  // also describes a pass that has already expired, and reading that as the
  // tier would nudge a lapsed SMB account to "Upgrade to Enterprise" instead of
  // prompting it to start a plan again.
  const tier = (livePlan(subscription)?.plan?.name || 'free').toLowerCase()
  const cfg = UPGRADE[tier]
  if (!cfg) return null // enterprise / unknown → no upsell

  return (
    <div className="px-2.5 pb-2">
      <div className="rounded-xl border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25 bg-gradient-to-b from-[#5e6ad2]/[0.06] to-transparent dark:from-[#5e6ad2]/[0.12] dark:to-transparent p-3.5">
        <div className="flex items-center gap-1.5">
          <Sparkle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
          <span
            className="text-[#0f1011] dark:text-[#f7f8f8]"
            style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
          >
            {cfg.title}
          </span>
        </div>
        <p
          className="mt-1.5 text-[#5b6068] dark:text-[#8a8f98] leading-relaxed"
          style={{ fontSize: '12px', letterSpacing: '-0.05px' }}
        >
          {cfg.sub}
        </p>
        <button
          type="button"
          onClick={() => navigate('/billing', { state: { scrollTo: 'plans' } })}
          className="mt-3 w-full h-9 rounded-lg text-white bg-gradient-to-r from-[#5e6ad2] to-[#7170ff] hover:from-[#6b76db] hover:to-[#8281ff] active:from-[#5058c0] active:to-[#6b6aff] shadow-[0_2px_8px_rgba(94,106,210,0.35)] transition-all duration-150"
          style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
        >
          {cfg.cta}
        </button>
      </div>
    </div>
  )
}

export default UpgradeCard
